import type { LicenseStatus } from '#layers/license/types/license';
import { SECONDS_PER_DAY } from '#layers/base/public';
import { EXPIRY_CRITICAL_DAYS, EXPIRY_WARNING_DAYS, GRACE_CRITICAL_DAYS } from '#layers/license/constants/expiry';

type LicenseWarningPhase = 'expiring' | 'grace';
type LicenseWarningSeverity = 'warning' | 'error';

export interface LicenseWarning {
	phase: LicenseWarningPhase;
	severity: LicenseWarningSeverity;
	daysLeft: number;
}

export interface DecideLicenseWarningInput {
	status: LicenseStatus | null;
	/** Unix seconds; `-1` = never expires. */
	expiresAt: number | null | undefined;
	renewsAt: number | null | undefined;
	/** Seconds of use after the deadline; `0` = none, `-1` = unlimited. */
	gracePeriod: number | null | undefined;
	now: number;
}

function daysUntil(target: number, now: number): number {
	return Math.max(0, Math.ceil((target - now) / SECONDS_PER_DAY));
}

function isConfigured(value: number | null | undefined): value is number {
	return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Decides whether to surface a license-expiry warning banner. Returns `null`
 * when no warning applies (locked/null status, non-expiring license, or an
 * active license still comfortably far from expiry). `locked` is owned by the
 * separate locked-state view.
 */
export function decideLicenseWarning(input: DecideLicenseWarningInput): LicenseWarning | null {
	const { status, expiresAt, renewsAt, gracePeriod, now } = input;

	if (status !== 'active' && status !== 'grace') return null;

	if (status === 'grace') {
		const anchor = isConfigured(expiresAt) ? expiresAt : renewsAt;
		if (!isConfigured(anchor)) return null;
		if (!isConfigured(gracePeriod)) return null;
		const daysLeft = daysUntil(anchor + gracePeriod, now);
		return { phase: 'grace', severity: daysLeft <= GRACE_CRITICAL_DAYS ? 'error' : 'warning', daysLeft };
	}

	// A renewing subscription rolls over on its own, so it never warns.
	if (isConfigured(renewsAt) || !isConfigured(expiresAt)) return null;
	const daysLeft = daysUntil(expiresAt, now);
	if (daysLeft > EXPIRY_WARNING_DAYS) return null;
	const hasNoGrace = gracePeriod === 0;
	const severity: LicenseWarningSeverity = daysLeft <= EXPIRY_CRITICAL_DAYS && hasNoGrace ? 'error' : 'warning';
	return { phase: 'expiring', severity, daysLeft };
}
